import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { directoryApi, queryKeys } from '@/lib/api';
import type { DepartmentNode } from '@/lib/api/types';
import type { SubjectDescriptor } from '@/lib/effective-permissions';

/**
 * Diretório de colaboradores e departamentos, COM o vínculo entre os dois.
 *
 * POR QUE ESTE MÓDULO EXISTE
 * A `<PermissionMatrix>` é a tela criada para o administrador conferir, antes
 * de salvar, qual permissão cada colaborador terá de fato. Ela avalia as regras
 * nos três alcances de `@qualicloud/domain/acl` — e o alcance DEPARTAMENTO só
 * funciona se cada sujeito souber a quais departamentos pertence. Enquanto os
 * sujeitos eram montados com `departmentIds: []`, toda regra de departamento
 * era silenciosamente ignorada: a ferramenta de verificação mentia sobre um dos
 * três alcances, o que é pior do que ela não existir.
 *
 * COMO O VÍNCULO É OBTIDO
 * `GET /users` não devolve os departamentos de cada colaborador e
 * `GET /departments` devolve só a árvore. A composição vem de
 * `GET /departments/:id`, que traz os membros. São N consultas pequenas,
 * disparadas em paralelo e cacheadas por 5 minutos — o diretório de uma
 * organização de dispositivos médicos tem dezenas de departamentos, não
 * milhares.
 *
 * DEGRADAÇÃO
 * `GET /users` exige `user:manage`. Quem não tem a permissão recebe 403 e
 * `subjects` fica vazio; por isso `forbidden` é devolvido à parte, para a tela
 * poder explicar em vez de mostrar uma matriz vazia sem motivo aparente.
 */
export interface Directory {
  readonly subjects: readonly SubjectDescriptor[];
  readonly departments: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly loading: boolean;
  /** Falha ao carregar o diretório — a tela explica em vez de mostrar lista vazia. */
  readonly forbidden: boolean;
}

const DIRECTORY_STALE_TIME_MS = 5 * 60_000;

export function useDirectory(): Directory {
  /*
   * UMA consulta.
   *
   * Antes eram `GET /users` + a árvore de departamentos + `GET /departments/:id`
   * para CADA departamento, só para descobrir quem pertence a quê — N+1 a cada
   * abertura de formulário. E `GET /users` exige `user:manage`, então o Gestor
   * da Qualidade recebia 403 e o seletor vinha vazio nas seis telas do SGQ.
   *
   * `GET /directory` devolve pessoa, cargo e lotação já resolvidos, e não exige
   * permissão de administração — ver o cabeçalho de
   * `ms-identity/src/modules/users/directory.controller.ts`.
   */
  const query = useQuery({
    queryKey: queryKeys.directory.all(),
    queryFn: () => directoryApi.find(),
    staleTime: DIRECTORY_STALE_TIME_MS,
    retry: false,
  });

  const subjects = useMemo<SubjectDescriptor[]>(
    () =>
      (query.data?.people ?? []).map((person) => ({
        userId: person.id,
        displayName: person.name,
        jobTitle: person.jobTitle,
        departmentIds: [...person.departmentIds],
      })),
    [query.data],
  );

  const departments = useMemo(() => [...(query.data?.departments ?? [])], [query.data]);

  return {
    subjects,
    departments,
    loading: query.isPending,
    forbidden: query.isError,
  };
}

/** Achata a árvore preservando o caminho hierárquico no rótulo. */
export function flattenDepartments(
  nodes: readonly DepartmentNode[],
  prefix = '',
): Array<{ id: string; name: string }> {
  const result: Array<{ id: string; name: string }> = [];
  for (const node of nodes) {
    const label = prefix ? `${prefix} › ${node.name}` : node.name;
    result.push({ id: node.id, name: label });
    if (node.children.length > 0) result.push(...flattenDepartments(node.children, label));
  }
  return result;
}
